import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { ThemedText, Button, ConfirmDialog } from '../components';
import certificateService from '../api/services/certificateService';
import { uploadFileToBlob } from '../api/services/storageService';
import axiosInstance, { apiRequest } from '../api/axiosInstance';
import { STORAGE_ENDPOINTS } from '../api/endpoints';
import { formatDateOnly } from '../utils/dateUtils';
import { Check, AlertTriangle, X, Award, ChevronRight } from 'lucide-react';

// ─── Styled Components ────────────────────────────────────────

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CertGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: ${props => props.theme.spacing.lg};
`;

const CertCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  position: relative;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: ${props => props.theme.colors.tint}40;
  }
`;

const CertCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const CertName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin: 0;
  flex: 1;
  margin-right: ${props => props.theme.spacing.md};
`;

const ExpiryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  background-color: ${props =>
    props.$status === 'Valid' ? '#28a74518' :
    props.$status === 'ExpiringSoon' ? '#ffc10718' :
    props.$status === 'Expired' ? '#dc354518' :
    `${props.theme.colors.border}40`
  };
  color: ${props =>
    props.$status === 'Valid' ? '#28a745' :
    props.$status === 'ExpiringSoon' ? '#e67e00' :
    props.$status === 'Expired' ? '#dc3545' :
    props.theme.colors.muted
  };
  border: 1px solid ${props =>
    props.$status === 'Valid' ? '#28a74530' :
    props.$status === 'ExpiringSoon' ? '#ffc10730' :
    props.$status === 'Expired' ? '#dc354530' :
    props.theme.colors.border
  };
`;

const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: 6px;
  font-size: 14px;
  color: ${props => props.theme.colors.muted};
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  min-width: 100px;
  font-size: 13px;
`;

const DaysCountdown = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  background-color: ${props =>
    props.$status === 'Valid' ? '#28a74510' :
    props.$status === 'ExpiringSoon' ? '#ffc10710' :
    props.$status === 'Expired' ? '#dc354510' :
    'transparent'
  };
  color: ${props =>
    props.$status === 'Valid' ? '#28a745' :
    props.$status === 'ExpiringSoon' ? '#e67e00' :
    props.$status === 'Expired' ? '#dc3545' :
    props.theme.colors.muted
  };
`;

const ScanThumbnail = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  cursor: pointer;
  border: 1px solid ${props => props.theme.colors.border};
  max-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.background};

  img {
    max-width: 100%;
    max-height: 120px;
    object-fit: contain;
  }

  &:hover {
    border-color: ${props => props.theme.colors.tint};
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 8px 16px;
  border: 1px solid ${props => props.$danger ? '#dc354540' : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$danger ? '#dc354508' : 'transparent'};
  color: ${props => props.$danger ? '#dc3545' : props.theme.colors.tint};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${props => props.$danger ? '#dc354515' : `${props.theme.colors.tint}10`};
    border-color: ${props => props.$danger ? '#dc3545' : props.theme.colors.tint};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.muted};
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  width: 100%;
  max-width: 560px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: 20px;
  font-weight: 700;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  font-size: 13px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.tint}15;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.$hasFile ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.$hasFile ? `${props.theme.colors.tint}08` : 'transparent'};

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}08;
  }
`;

const UploadPreview = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  img {
    max-height: 60px;
    max-width: 100px;
    object-fit: contain;
    border-radius: 4px;
    border: 1px solid ${props => props.theme.colors.border};
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${props => props.theme.colors.border};
  border-radius: 2px;
  margin-top: ${props => props.theme.spacing.sm};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.colors.tint};
  border-radius: 2px;
  transition: width 0.3s ease;
  width: ${props => props.$progress}%;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.danger};
  cursor: pointer;
  font-size: 12px;
  padding: 2px 6px;

  &:hover {
    text-decoration: underline;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

// Image preview (lightbox)
const LightboxOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: pointer;
`;

const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
`;

// ─── Helpers ──────────────────────────────────────────────────

const getExpiryLabel = (status) => {
  switch (status) {
    case 'Valid': return 'Valid';
    case 'ExpiringSoon': return 'Expiring Soon';
    case 'Expired': return 'Expired';
    default: return 'No Expiry';
  }
};

const getExpiryIcon = (status) => {
  switch (status) {
    case 'Valid': return <Check size={14} />;
    case 'ExpiringSoon': return <AlertTriangle size={14} />;
    case 'Expired': return <X size={14} />;
    default: return '\u2014';
  }
};

// ─── Component ────────────────────────────────────────────────

export const MyCertificatesSection = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [myCerts, setMyCerts] = useState([]);
  const [certDefinitions, setCertDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [scanUrls, setScanUrls] = useState({});
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Upload state
  const [scanFile, setScanFile] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const [certForm, setCertForm] = useState({
    certificateDefinitionId: '',
    certificateNumber: '',
    issueDate: '',
    expiryDate: '',
    issuingAuthority: '',
    scanImagePath: ''
  });

  const fetchMyCerts = useCallback(async () => {
    try {
      const response = await certificateService.getMyCertificates();
      setMyCerts(Array.isArray(response) ? response : (response?.data || []));
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCertDefinitions = useCallback(async () => {
    try {
      const response = await certificateService.getDefinitions();
      setCertDefinitions(Array.isArray(response) ? response : (response?.data || []));
    } catch (error) {
      console.error('Failed to fetch certificate definitions:', error);
    }
  }, []);

  useEffect(() => {
    fetchMyCerts();
    fetchCertDefinitions();
  }, [fetchMyCerts, fetchCertDefinitions]);

  // Resolve blob paths to signed URLs using a single read SAS token
  useEffect(() => {
    const resolveScanUrls = async () => {
      const certsWithScans = myCerts.filter(c => c.scanImagePath);
      if (certsWithScans.length === 0) return;

      try {
        const sasResponse = await apiRequest(() =>
          axiosInstance.get(`${STORAGE_ENDPOINTS.SAS_TOKEN}?permissions=r&durationMinutes=60`)
        );
        const containerUrl = sasResponse?.containerUrl;
        if (!containerUrl) return;

        const baseUrl = containerUrl.split('?')[0];
        const sasToken = containerUrl.split('?')[1];
        const newUrls = {};
        certsWithScans.forEach(cert => {
          newUrls[cert.id] = `${baseUrl}/${cert.scanImagePath}?${sasToken}`;
        });
        setScanUrls(newUrls);
      } catch (error) {
        console.error('Failed to resolve scan URLs:', error);
      }
    };
    resolveScanUrls();
  }, [myCerts]);

  const availableDefinitions = certDefinitions.filter(
    def => !myCerts.some(c => c.certificateDefinitionId === def.id)
  );

  const handleOpenAdd = () => {
    setEditingCert(null);
    setCertForm({
      certificateDefinitionId: '',
      certificateNumber: '',
      issueDate: '',
      expiryDate: '',
      issuingAuthority: '',
      scanImagePath: ''
    });
    setScanFile(null);
    setScanPreview(null);
    setUploadProgress(0);
    setModalOpen(true);
  };

  const handleOpenEdit = (cert) => {
    setEditingCert(cert);
    setCertForm({
      certificateDefinitionId: cert.certificateDefinitionId,
      certificateNumber: cert.certificateNumber || '',
      issueDate: cert.issueDate ? cert.issueDate.split('T')[0] : '',
      expiryDate: cert.expiryDate ? cert.expiryDate.split('T')[0] : '',
      issuingAuthority: cert.issuingAuthority || '',
      scanImagePath: cert.scanImagePath || ''
    });
    setScanFile(null);
    setScanPreview(scanUrls[cert.id] || null);
    setUploadProgress(0);
    setModalOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select an image (JPG, PNG, GIF, WebP) or PDF file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB.');
      return;
    }

    setScanFile(file);
    if (file.type.startsWith('image/')) {
      setScanPreview(URL.createObjectURL(file));
    } else {
      setScanPreview(null);
    }
  };

  const handleRemoveScan = () => {
    setScanFile(null);
    setScanPreview(null);
    setCertForm(prev => ({ ...prev, scanImagePath: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadScanFile = async () => {
    if (!scanFile) return certForm.scanImagePath;

    try {
      setUploading(true);
      setUploadProgress(0);

      const { blobPath } = await uploadFileToBlob(scanFile, (_, __, pct) => {
        setUploadProgress(pct);
      });

      return blobPath;
    } catch (error) {
      console.error('Failed to upload scan:', error);
      toast.error('Failed to upload scan image');
      return certForm.scanImagePath;
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!editingCert && !certForm.certificateDefinitionId) {
      toast.error('Please select a certificate type');
      return;
    }

    try {
      setSaving(true);
      const uploadedPath = await uploadScanFile();

      if (editingCert) {
        await certificateService.updateMyCertificate(editingCert.id, {
          certificateNumber: certForm.certificateNumber || null,
          issueDate: certForm.issueDate || null,
          expiryDate: certForm.expiryDate || null,
          issuingAuthority: certForm.issuingAuthority || null,
          scanImagePath: uploadedPath || null
        });
        toast.success('Certificate updated');
      } else {
        await certificateService.addMyCertificate({
          certificateDefinitionId: parseInt(certForm.certificateDefinitionId),
          certificateNumber: certForm.certificateNumber || null,
          issueDate: certForm.issueDate || null,
          expiryDate: certForm.expiryDate || null,
          issuingAuthority: certForm.issuingAuthority || null,
          scanImagePath: uploadedPath || null
        });
        toast.success('Certificate added');
      }
      setModalOpen(false);
      fetchMyCerts();
    } catch (error) {
      console.error('Failed to save certificate:', error);
      toast.error(error.response?.data?.message || 'Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteConfirmId(null);
    try {
      await certificateService.deleteMyCertificate(id);
      toast.success('Certificate deleted');
      fetchMyCerts();
    } catch (error) {
      toast.error('Failed to delete certificate');
    }
  };

  return (
    <>
      <PageContainer style={{ maxWidth: '100%' }}>
        <PageHeader theme={theme} style={{ alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#43474f', fontWeight: 500, marginBottom: 10 }}>
              <span>Operations</span>
              <ChevronRight size={12} />
              <span style={{ color: '#232562', fontWeight: 700 }}>My Certificates</span>
            </div>
            <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1.875rem', fontWeight: 800, color: '#232562', margin: '0 0 6px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>My Certificates</h1>
          </div>
          <Button variant="primary" onClick={handleOpenAdd}>
            + Add Certificate
          </Button>
        </PageHeader>

        {loading ? (
          <ThemedText style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.muted }}>
            Loading certificates...
          </ThemedText>
        ) : myCerts.length === 0 ? (
          <EmptyState theme={theme}>
            <EmptyIcon><Award size={48} /></EmptyIcon>
            <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.sm }}>
              No Certificates Yet
            </ThemedText>
            <ThemedText style={{ color: theme.colors.muted, marginBottom: theme.spacing.lg }}>
              Add your professional certifications and credentials to keep them organized and track expiry dates.
            </ThemedText>
            <Button variant="primary" onClick={handleOpenAdd}>
              + Add Your First Certificate
            </Button>
          </EmptyState>
        ) : (
          <CertGrid theme={theme}>
            {myCerts.map(cert => (
              <CertCard key={cert.id} theme={theme}>
                <CertCardTop theme={theme}>
                  <CertName theme={theme}>{cert.certificateDefinitionName}</CertName>
                  <ExpiryBadge $status={cert.expiryStatus} theme={theme}>
                    {getExpiryIcon(cert.expiryStatus)} {getExpiryLabel(cert.expiryStatus)}
                  </ExpiryBadge>
                </CertCardTop>

                {cert.certificateNumber && (
                  <DetailRow theme={theme}>
                    <DetailLabel theme={theme}>Number:</DetailLabel>
                    <span>{cert.certificateNumber}</span>
                  </DetailRow>
                )}
                {cert.issuingAuthority && (
                  <DetailRow theme={theme}>
                    <DetailLabel theme={theme}>Issued by:</DetailLabel>
                    <span>{cert.issuingAuthority}</span>
                  </DetailRow>
                )}
                {cert.issueDate && (
                  <DetailRow theme={theme}>
                    <DetailLabel theme={theme}>Issue Date:</DetailLabel>
                    <span>{formatDateOnly(cert.issueDate)}</span>
                  </DetailRow>
                )}
                {cert.expiryDate && (
                  <DetailRow theme={theme}>
                    <DetailLabel theme={theme}>Expiry Date:</DetailLabel>
                    <span>{formatDateOnly(cert.expiryDate)}</span>
                  </DetailRow>
                )}

                {cert.expiryDate && cert.daysUntilExpiry != null && (
                  <DaysCountdown $status={cert.expiryStatus} theme={theme}>
                    {cert.daysUntilExpiry >= 0
                      ? `${cert.daysUntilExpiry} days until expiry`
                      : `Expired ${Math.abs(cert.daysUntilExpiry)} days ago`
                    }
                  </DaysCountdown>
                )}

                {cert.scanImagePath && (
                  <ScanThumbnail theme={theme} onClick={() => scanUrls[cert.id] && setLightboxUrl(scanUrls[cert.id])}>
                    {scanUrls[cert.id] ? (
                      <img src={scanUrls[cert.id]} alt="Certificate scan" />
                    ) : (
                      <span style={{ fontSize: '13px', color: theme.colors.muted, padding: '12px' }}>Loading scan...</span>
                    )}
                  </ScanThumbnail>
                )}

                <CardActions theme={theme}>
                  <ActionButton theme={theme} onClick={() => handleOpenEdit(cert)}>
                    Edit
                  </ActionButton>
                  <ActionButton theme={theme} $danger onClick={() => setDeleteConfirmId(cert.id)}>
                    Delete
                  </ActionButton>
                </CardActions>
              </CertCard>
            ))}
          </CertGrid>
        )}

        {/* Add/Edit Modal */}
        {modalOpen && (
          <ModalOverlay onClick={() => !saving && setModalOpen(false)}>
            <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
              <ModalTitle theme={theme}>
                {editingCert ? 'Edit Certificate' : 'Add Certificate'}
              </ModalTitle>

              {!editingCert && (
                <FormGroup theme={theme}>
                  <FormLabel theme={theme}>Certificate Type *</FormLabel>
                  <FormSelect
                    theme={theme}
                    value={certForm.certificateDefinitionId}
                    onChange={(e) => setCertForm(prev => ({ ...prev, certificateDefinitionId: e.target.value }))}
                  >
                    <option value="">Select a certificate type...</option>
                    {availableDefinitions.map(def => (
                      <option key={def.id} value={def.id}>{def.name}</option>
                    ))}
                  </FormSelect>
                </FormGroup>
              )}

              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Certificate Number</FormLabel>
                <FormInput
                  theme={theme}
                  value={certForm.certificateNumber}
                  onChange={(e) => setCertForm(prev => ({ ...prev, certificateNumber: e.target.value }))}
                  placeholder="e.g., FA-2024-001"
                  maxLength={100}
                />
              </FormGroup>

              <FormRow theme={theme}>
                <FormGroup theme={theme}>
                  <FormLabel theme={theme}>Issue Date</FormLabel>
                  <FormInput
                    theme={theme}
                    type="date"
                    value={certForm.issueDate}
                    onChange={(e) => setCertForm(prev => ({ ...prev, issueDate: e.target.value }))}
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <FormLabel theme={theme}>Expiry Date</FormLabel>
                  <FormInput
                    theme={theme}
                    type="date"
                    value={certForm.expiryDate}
                    onChange={(e) => setCertForm(prev => ({ ...prev, expiryDate: e.target.value }))}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Issuing Authority</FormLabel>
                <FormInput
                  theme={theme}
                  value={certForm.issuingAuthority}
                  onChange={(e) => setCertForm(prev => ({ ...prev, issuingAuthority: e.target.value }))}
                  placeholder="e.g., Red Cross, OSHA"
                  maxLength={200}
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Scan / Photo</FormLabel>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <UploadArea
                  theme={theme}
                  $hasFile={!!scanFile || !!scanPreview}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {scanFile ? (
                    <div>
                      <div style={{ fontSize: '13px', color: theme.colors.text, fontWeight: 600 }}>{scanFile.name}</div>
                      <div style={{ fontSize: '12px', color: theme.colors.muted }}>{(scanFile.size / 1024).toFixed(0)} KB</div>
                    </div>
                  ) : scanPreview ? (
                    <div style={{ fontSize: '13px', color: theme.colors.tint }}>Existing scan attached (click to replace)</div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '24px', marginBottom: '4px' }}>📎</div>
                      <div style={{ fontSize: '13px', color: theme.colors.muted }}>Click to upload scan or photo</div>
                      <div style={{ fontSize: '11px', color: theme.colors.muted }}>JPG, PNG, GIF, WebP, or PDF (max 10MB)</div>
                    </div>
                  )}
                </UploadArea>
                {(scanFile || scanPreview) && (
                  <UploadPreview theme={theme}>
                    {scanPreview && scanPreview.startsWith('blob:') && (
                      <img src={scanPreview} alt="Preview" />
                    )}
                    {scanPreview && !scanPreview.startsWith('blob:') && !scanFile && (
                      <img src={scanPreview} alt="Existing scan" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    <RemoveFileButton theme={theme} type="button" onClick={handleRemoveScan}>
                      Remove
                    </RemoveFileButton>
                  </UploadPreview>
                )}
                {uploading && (
                  <ProgressBar theme={theme}>
                    <ProgressFill theme={theme} $progress={uploadProgress} />
                  </ProgressBar>
                )}
              </FormGroup>

              <ModalActions theme={theme}>
                <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || uploading || (!editingCert && !certForm.certificateDefinitionId)}
                >
                  {saving ? 'Saving...' : editingCert ? 'Update Certificate' : 'Add Certificate'}
                </Button>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Image Lightbox */}
        {lightboxUrl && (
          <LightboxOverlay onClick={() => setLightboxUrl(null)}>
            <LightboxImage src={lightboxUrl} alt="Certificate scan" onClick={(e) => e.stopPropagation()} />
          </LightboxOverlay>
        )}
        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Confirm Delete"
          message="Are you sure you want to delete this certificate?"
          confirmLabel="Delete"
          onConfirm={() => handleDelete(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      </PageContainer>
    </>
  );
};

export const MyCertificatesPage = () => (
  <Layout currentPath="/my-certificates">
    <MyCertificatesSection />
  </Layout>
);
